import { useState, useEffect } from 'react';
import { Calendar, MapPin, Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { fetchAllEvents, createEvent, updateEvent, deleteEvent } from '../services/eventsService';
import { EventItem, NewEvent } from '../types';
import { getExperiences } from '../services/legacyApiService';
import { LegacyExperience } from '../types/legacy';

const Events = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventItem | null>(null);
  const [experienceOptions, setExperienceOptions] = useState<LegacyExperience[]>([]);
  const [formData, setFormData] = useState<NewEvent>({
    experienceId: '',
    experienceName: '',
    title: '',
    description: '',
    location: '',
    imageUrl: '',
    startAt: '',
    endAt: '',
    allDay: false,
    category: '',
    tags: [],
    price: '',
    organizer: '',
    contactInfo: '',
    website: '',
  });

  useEffect(() => {
    loadEvents();
    loadExperienceOptions();
  }, []);

  const loadExperienceOptions = async () => {
    try {
      const list = await getExperiences();
      setExperienceOptions(list || []);
    } catch (error) {
      console.error('Errore nel caricamento delle esperienze:', error);
      setExperienceOptions([]);
    }
  };

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const allEvents = await fetchAllEvents();
      setEvents(allEvents);
    } catch (error) {
      console.error('Errore nel caricamento degli eventi:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, formData);
      } else {
        await createEvent(formData);
      }
      setIsModalOpen(false);
      setEditingEvent(null);
      setFormData({
        experienceId: '',
        experienceName: '',
        title: '',
        description: '',
        location: '',
        imageUrl: '',
        startAt: '',
        endAt: '',
        allDay: false,
        category: '',
        tags: [],
        price: '',
        organizer: '',
        contactInfo: '',
        website: '',
      });
      loadEvents();
    } catch (error) {
      console.error('Errore nel salvataggio dell\'evento:', error);
    }
  };

  const handleEdit = (event: EventItem) => {
    setEditingEvent(event);
    setFormData({
      experienceId: String(event.experienceId || ''),
      experienceName: event.experienceName || '',
      title: event.title,
      description: event.description,
      location: event.location,
      imageUrl: event.imageUrl,
      startAt: event.startAt.toISOString().slice(0, 16),
      endAt: event.endAt ? event.endAt.toISOString().slice(0, 16) : '',
      allDay: event.allDay,
      category: event.category || '',
      tags: event.tags || [],
      price: event.price || '',
      organizer: event.organizer || '',
      contactInfo: event.contactInfo || '',
      website: event.website || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (eventId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
      try {
        await deleteEvent(eventId);
        loadEvents();
      } catch (error) {
        console.error('Errore nell\'eliminazione dell\'evento:', error);
      }
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container-max section-padding py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-2">
              Gestione Eventi
            </h1>
            <p className="text-text-secondary">
              Gestisci gli eventi di Glooci
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="mt-4 md:mt-0 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-secondary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            <Plus size={18} />
            Nuovo Evento
          </button>
        </div>

        {/* Events List */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <div key={event.id} className="card-hover p-6">
                <div className="aspect-video bg-gray-200 rounded-lg mb-4 overflow-hidden">
                  <img 
                    src={event.imageUrl || '/placeholder-event.jpg'} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Calendar size={16} />
                    <span>{formatDate(event.startAt)}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary line-clamp-2">
                    {event.title}
                  </h3>
                  <p className="text-text-secondary text-sm line-clamp-3">
                    {event.description}
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <MapPin size={16} />
                    <span>{event.location}</span>
                  </div>
                  {event.website && (
                    <a
                      href={event.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-primary hover:text-secondary text-sm transition-colors duration-200"
                    >
                      <ExternalLink size={16} />
                      <span>Visita il sito</span>
                    </a>
                  )}
                  <div className="flex items-center space-x-2 pt-4 border-t border-border">
                    <button
                      onClick={() => handleEdit(event)}
                      className="flex items-center space-x-1 text-primary hover:text-secondary transition-colors duration-200"
                    >
                      <Edit size={16} />
                      <span className="text-sm">Modifica</span>
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="flex items-center space-x-1 text-error hover:text-error/80 transition-colors duration-200"
                    >
                      <Trash2 size={16} />
                      <span className="text-sm">Elimina</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-text-primary">
                    {editingEvent ? 'Modifica Evento' : 'Nuovo Evento'}
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingEvent(null);
                    }}
                    className="text-text-secondary hover:text-text-primary"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Esperienza associata (facoltativa)
                    </label>
                    <select
                      value={formData.experienceId || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedExperience = experienceOptions.find((exp) => String(exp.id) === selectedId);
                        setFormData({
                          ...formData,
                          experienceId: selectedId,
                          experienceName: selectedExperience ? String(selectedExperience.name || '') : '',
                        });
                      }}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    >
                      <option value="">Nessuna esperienza (non visibile in app mobile)</option>
                      {experienceOptions.map((exp) => (
                        <option key={exp.id} value={String(exp.id)}>
                          #{exp.id} - {exp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Titolo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Categoria
                    </label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Descrizione
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Luogo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      URL Immagine
                    </label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Data e Ora Inizio *
                    </label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.startAt}
                      onChange={(e) => setFormData({ ...formData, startAt: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Data e Ora Fine
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.endAt}
                      onChange={(e) => setFormData({ ...formData, endAt: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="allDay"
                    checked={formData.allDay}
                    onChange={(e) => setFormData({ ...formData, allDay: e.target.checked })}
                    className="rounded border-border text-primary focus:ring-primary/50"
                  />
                  <label htmlFor="allDay" className="text-sm font-medium text-text-primary">
                    Evento per tutta la giornata
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Prezzo
                    </label>
                    <input
                      type="text"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="es. Gratuito, €10, €15-20"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Organizzatore
                    </label>
                    <input
                      type="text"
                      value={formData.organizer}
                      onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Sito Web
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>

                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingEvent(null);
                    }}
                    className="px-6 py-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingEvent ? 'Aggiorna Evento' : 'Crea Evento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
